import React, { useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Connector,
  EConnectorFields,
  IConnectorActionsGroupProps,
  IConnectorButtonGroupProps,
  IConnectorHostURLProps,
  IConnectorCredentialsFormProps,
  ICredentialsFormSwitchProps,
} from '../connectors-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ConnectorHostURL from './ConnectorHostURL';
import CredentialsFormSwitch from './ConnectorCredentialsSwitch';
import ConnectorButtonGroup from './ConnectorButtonGroup';
import { shouldDisableConnect } from '../utils/configure-form-utils';
import { updateSelectedConnectorField } from '../actions/connector-configure-actions';
import { checkWhichTabToShow } from '../actions/credentials-actions';
import { redirectToIDP } from '../actions/connector-configure-form-actions';
import { updateSingleConnectorsField } from '../store-actions/connectors-store-actions';
import {
  CredentialsType,
  CredentialsTypeTitle,
} from '../connector-configure-types';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import ConnectorInstructions from './ConnectorConnectionInstructions';

const CredentialsTab: React.FC<
  React.PropsWithChildren<CredentialsTabContextProps>
> = ({
  connector,
  connectorHostURLProps,
  connectorCredentialsFormProps,
  connectorActionsGroupProps,
  isConnectorConfigured,
  showSingleValidationMsg,
  connectorIsValid,
  isScanning,
}) => {
  const { credentialsType, credentialsTypes } = connector;
  const [tabContext, setValueTabContext] = React.useState(
    credentialsTypes ? credentialsTypes[0] : credentialsType,
  );

  useEffect(() => {
    // TODO: remove once we're supporting more than 1 credentials or when we remove tabs
    const startingTab = checkWhichTabToShow();
    if (startingTab) {
      setValueTabContext(startingTab);
    }
    return () => {
      updateSelectedConnectorField(
        connector.id,
        EConnectorFields.errorMessage,
        '',
      );
      updateSingleConnectorsField(
        connector.id,
        EConnectorFields.errorMessage,
        '',
      );
    };
  }, [connector.id]);

  useEffect(() => {
    if (connector.errorMessage) {
      openSnackbar(connector.errorMessage, { variant: 'error' });
    }
  }, [connector.errorMessage, connector.id]);

  const credentialsFormSwitchProps: ICredentialsFormSwitchProps = {
    credentialsType: tabContext,
    credentialsFormProps: {
      ...connectorCredentialsFormProps,
      credentialsType: tabContext,
    },
  };

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: CredentialsType) => {
      updateSelectedConnectorField(
        connector.id,
        EConnectorFields.credentialsType,
        newValue,
      );
      updateSelectedConnectorField(
        connector.id,
        EConnectorFields.errorMessage,
        '',
      );
      setValueTabContext(newValue);
    },
    [connector.id],
  );

  const onConnectRedirect = useCallback(() => {
    redirectToIDP(connector);
  }, [connector]);

  const {
    onVerifyAndAdd,
    onRemoveCredentials,
    connectorValidationInProgress,
    validateConnector,
    openConnectorSettings,
  } = connectorActionsGroupProps;

  const connectorButtonGroupProps: IConnectorButtonGroupProps = {
    onVerifyAndAdd:
      tabContext === CredentialsType.IdentityProvider
        ? onConnectRedirect
        : onVerifyAndAdd,
    onRemoveCredentials,
    disabledConnect: shouldDisableConnect(tabContext),
    isConnectorConfigured,
    validateConnector,
    connectorValidationInProgress,
    showValidationResult: showSingleValidationMsg,
    connectorIsValid,
    openConnectorSettings,
    isScanning,
  };
  const connectorRequireHostURL = ![
    CredentialsType.AWSAssumeRole,
    CredentialsType.AWSAssumeRoleCloudFormation,
    CredentialsType.AWSOrganizationConnection,
    CredentialsType.AWSAssumeRoleOnprem,
    CredentialsType.AWSAssumeRoleCloudFormationOnprem,
    CredentialsType.AWSOrganizationConnectionOnprem,
    CredentialsType.ClientIdSecretKey,
    CredentialsType.IdentityProvider,
  ].includes(tabContext);
  const {
    credentialsFormProps: { areCredentialsExpired, credentialsExpirationMsg },
  } = credentialsFormSwitchProps;
  return (
    <TabContext value={tabContext}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <TabList onChange={handleChange} aria-label='Credentials-tabs'>
          {(credentialsTypes || [credentialsType]).map(
            (type: CredentialsType) => {
              return (
                <Tab
                  key={type}
                  label={CredentialsTypeTitle[type]}
                  value={type}
                />
              );
            },
          )}
        </TabList>
      </Box>
      <Stack minWidth='100%' direction='column' spacing={2}>
        <TabPanel
          sx={{ paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
          value={tabContext}>
          <Stack justifyContent='space-around' direction='column'>
            {![CredentialsType.None, CredentialsType.IdentityProvider].includes(
              tabContext,
            ) &&
              connectorHostURLProps && (
                <Stack minHeight={connectorRequireHostURL ? 150 : undefined}>
                  {connector.connectionInstructions && (
                    <ConnectorInstructions
                      connectionInstructions={connector.connectionInstructions}
                      credentialsType={tabContext}
                    />
                  )}
                  {connectorRequireHostURL && (
                    <ConnectorHostURL {...connectorHostURLProps} />
                  )}
                  <CredentialsFormSwitch {...credentialsFormSwitchProps} />
                </Stack>
              )}
            {tabContext === CredentialsType.IdentityProvider && (
              <Box minHeight={50}>
                {!areCredentialsExpired && (
                  <>
                    <Typography
                      variant='caption'
                      color={
                        isConnectorConfigured ? 'success.main' : 'text.primary'
                      }>
                      {isConnectorConfigured
                        ? `${connector.displayName} connector is configured. You can
                    press "Verify Connectivity" below to check your credentials.`
                        : connector.identityProviderInfo?.configText}
                    </Typography>
                    {!isConnectorConfigured && (
                      <Typography variant='body2' color='info' mt={1}>
                        <b>
                          <u>Note</u>: you need to explicitly approve access to
                          organization resources.
                        </b>
                      </Typography>
                    )}
                  </>
                )}
                {areCredentialsExpired && (
                  <Typography variant='caption' color='error.main'>
                    {credentialsExpirationMsg}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </TabPanel>
        <ConnectorButtonGroup {...connectorButtonGroupProps} />
      </Stack>
    </TabContext>
  );
};

interface CredentialsTabContextProps {
  connector: Connector;
  connectorHostURLProps?: IConnectorHostURLProps;
  connectorCredentialsFormProps: IConnectorCredentialsFormProps;
  connectorActionsGroupProps: IConnectorActionsGroupProps;
  isConnectorConfigured: boolean;
  showSingleValidationMsg: boolean;
  connectorIsValid: boolean;
  isScanning: boolean;
}

export default CredentialsTab;
